import React, { Fragment, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import Badge from "../../components/ui-components/Badge";
import Modal from "../../components/ui-components/Modal";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import { toast } from "../../components/ui-components/Toast";
import { listIngestTokens, createIngestToken, revokeIngestToken } from "./litellmIngestTokensApi";

// fmtDate renders an ISO timestamp as a short local date-time. Returns "—" for empty input.
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const CALLBACK_HOST = "https://backend.instantview.ai";

// CopyField renders a read-only value with a Copy button. Used for the secret, URL, and header.
const CopyField = ({ value, mono }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy — clipboard access blocked");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <code className={`flex-1 bg-surface-2 border border-card-bd rounded-ctrl px-3 py-2 text-sm break-all ${mono ? "font-mono" : ""}`}>
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        className="px-3 py-2 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors shrink-0"
      >
        Copy
      </button>
    </div>
  );
};

const StatusBadge = ({ status }) =>
  status === "revoked"
    ? <Badge label="Revoked" status="cancelled" size="sm" />
    : <Badge label="Active" status="success" size="sm" />;

const LitellmIngestTokensPage = () => {
  const queryClient = useQueryClient();
  const { data: tokens = [], isLoading, error } = useQuery(
    ["litellm-ingest-tokens"],
    listIngestTokens
  );

  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState("");
  const [revealed, setRevealed] = useState(null); // the one-time secret object; never persisted
  const [revokeTarget, setRevokeTarget] = useState(null);

  const notEnabled = error?.status === 503;

  const createMutation = useMutation(() => createIngestToken(label.trim()), {
    onSuccess: (created) => {
      setShowCreate(false);
      setLabel("");
      setRevealed(created);          // secret lives only here
      queryClient.invalidateQueries(["litellm-ingest-tokens"]);
    },
    onError: () => toast.error("Couldn't generate the token. Please try again."),
  });

  const revokeMutation = useMutation((id) => revokeIngestToken(id), {
    onSuccess: () => {
      toast.success("Token revoked");
      queryClient.invalidateQueries(["litellm-ingest-tokens"]);
      setRevokeTarget(null);
    },
    onError: () => toast.error("Couldn't revoke the token. Please try again."),
  });

  const closeReveal = () => setRevealed(null);

  const openCreate = () => { setLabel(""); setShowCreate(true); };

  const columns = useMemo(
    () => [
      {
        accessor: "display",
        Header: () => <span>Token</span>,
        Cell: ({ value }) => <span className="font-mono text-text truncate">{value}</span>,
        flex: 1,
        minWidth: 200,
      },
      {
        accessor: "label",
        Header: () => <span>Label</span>,
        Cell: ({ value }) =>
          value ? <span className="text-text-2 truncate">{value}</span> : <span className="text-text-3">—</span>,
        flex: 1,
        minWidth: 140,
      },
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusBadge status={value} />,
        width: 110,
        minWidth: 100,
      },
      {
        accessor: "created_at",
        Header: () => <span>Created</span>,
        Cell: ({ value }) => <span className="text-text-2">{fmtDate(value)}</span>,
        width: 170,
        minWidth: 140,
      },
      {
        accessor: "last_used_at",
        Header: () => <span>Last used</span>,
        Cell: ({ value }) =>
          value ? <span className="text-text-2">{fmtDate(value)}</span> : <span className="text-text-3">Never used</span>,
        width: 170,
        minWidth: 140,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        Cell: ({ row }) =>
          row.original.status === "revoked" ? null : (
            <button
              type="button"
              onClick={() => setRevokeTarget(row.original)}
              className="px-3 py-1 text-sm rounded-ctrl text-danger hover:bg-danger-soft transition-colors"
            >
              Revoke
            </button>
          ),
        width: 110,
        minWidth: 100,
      },
    ],
    []
  );

  const noun = tokens.length === 1 ? "token" : "tokens";

  return (
    <Fragment>
      <AdministrationHeader title="Ingest Tokens" showPeriodSelector={false} />
      <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6">
        <p className="text-sm text-text-2 mb-4 max-w-2xl">
          Mint and manage the tokens your LiteLLM proxy uses to authenticate to our ingestion
          receiver. The secret is shown once at creation — store it in your proxy configuration.
        </p>

        {notEnabled ? (
          <div className="bg-card border border-card-bd rounded-card px-6 py-12 text-center">
            <div className="text-sm font-semibold text-text mb-1">LiteLLM ingestion isn't enabled yet</div>
            <p className="text-sm text-text-2">
              Token minting becomes available once LiteLLM ingestion is configured for your account.
            </p>
          </div>
        ) : (
          <TableCard
            title="Ingest tokens"
            subtitle={isLoading ? "Loading…" : `Showing ${tokens.length} ${noun}`}
            primaryAction={{ label: "Generate token", icon: icon({ name: "plus" }), onClick: openCreate }}
            isLoading={isLoading}
            error={error}
            errorMessage="Error loading ingest tokens"
            onRetry={() => queryClient.invalidateQueries(["litellm-ingest-tokens"])}
          >
            {tokens.length === 0 ? (
              <EmptyState
                icon={icon({ name: "key", style: "light" })}
                title="No ingest tokens yet"
                message="Generate a token to connect your LiteLLM proxy to our ingestion receiver."
                action={{ label: "Generate token", onClick: openCreate, icon: icon({ name: "plus", style: "regular" }) }}
              />
            ) : (
              <FlexTable columns={columns} data={tokens} getRowId={(t) => t.id} />
            )}
          </TableCard>
        )}
      </div>

      {/* Create modal */}
      <Modal
        show={showCreate}
        close={() => setShowCreate(false)}
        title="Generate ingest token"
        description="Optionally label this token so you can tell your proxies apart. The secret is shown once."
        icon={icon({ name: "key", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={createMutation.isLoading ? "Generating…" : "Generate"}
        confirmAction={() => { if (!createMutation.isLoading) createMutation.mutate(); }}
      >
        <div className="mb-4">
          <label htmlFor="ingest-token-label" className="block mb-1 text-sm font-medium text-text">
            Label (optional)
          </label>
          <input
            id="ingest-token-label"
            type="text"
            value={label}
            maxLength={100}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="prod proxy"
            className="border border-border rounded-ctrl px-3 py-1.5 text-sm w-72 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </Modal>

      {/* One-time reveal modal — explicit dismiss only */}
      <Modal
        show={!!revealed}
        close={closeReveal}
        title="Copy your ingest token now"
        description="This is the only time the secret is shown. Paste it into your LiteLLM proxy before closing."
        icon={icon({ name: "key", style: "solid" })}
        cancelButton=""
        confirmButton="Done"
        confirmAction={closeReveal}
        size="lg"
      >
        {revealed && (
          <div className="flex flex-col gap-4">
            <div>
              <div className="text-sm font-medium text-text mb-1">Token (secret)</div>
              <CopyField value={revealed.token} mono />
              <p className="text-xs font-semibold text-danger mt-1">
                Copy this now — you will not be able to see it again.
              </p>
            </div>
            <div>
              <div className="text-sm font-medium text-text mb-1">Callback URL</div>
              <CopyField value={`${CALLBACK_HOST}${revealed.callback_path}`} />
            </div>
            <div>
              <div className="text-sm font-medium text-text mb-1">Header name</div>
              <CopyField value={revealed.header} mono />
              <p className="text-xs text-text-2 mt-1">
                Configure your proxy to send the token as the value of this header.
              </p>
            </div>
          </div>
        )}
      </Modal>
      {/* Revoke confirm */}
      <Modal
        show={!!revokeTarget}
        close={() => setRevokeTarget(null)}
        title="Revoke this token?"
        description={
          <span>
            Revoking takes effect immediately and breaks any proxy still using
            {revokeTarget?.label ? ` "${revokeTarget.label}"` : " this token"}.
            To rotate without downtime: create a new token, update your proxy's header to the new
            value, then revoke the old one.
          </span>
        }
        alertIcon
        icon={icon({ name: "ban", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={revokeMutation.isLoading ? "Revoking…" : "Revoke token"}
        confirmAction={() => { if (!revokeMutation.isLoading && revokeTarget) revokeMutation.mutate(revokeTarget.id); }}
      />
    </Fragment>
  );
};

export default LitellmIngestTokensPage;
